import React, { useEffect, useMemo, useState } from "react";
import { FileText, Plus, Trash2, Printer, Save, X, ChevronRight, Loader2, Search, Download } from "lucide-react";
import { api } from "../api";

const CURRENCIES = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "JPY", symbol: "¥" },
  { code: "AUD", symbol: "A$" },
  { code: "CAD", symbol: "C$" },
];

const STATUS_COLORS = {
  draft: "#94a3b8", sent: "#38bdf8", accepted: "#22c55e", declined: "#ef4444", expired: "#fb7185",
};

const EMPTY = () => ({
  quote_number: "",
  title: "",
  client_name: "",
  client_email: "",
  client_company: "",
  client_address: "",
  issue_date: new Date().toISOString().slice(0, 10),
  valid_until: "",
  currency: "INR",
  items: [{ description: "", quantity: 1, unit_price: 0, tax_percent: 0 }],
  notes: "",
  terms: "Payment due within 30 days. All prices exclude applicable taxes unless stated.",
  status: "draft",
  discount_percent: 0,
  tax_percent: 0,
});

export default function Quotation() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // full quote object being edited
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotations");
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(i => (i.quote_number + " " + i.client_name + " " + i.title).toLowerCase().includes(s));
  }, [items, q]);

  const startNew = () => { setSelected(EMPTY()); setCreating(true); };
  const openQuote = (item) => { setSelected(item); setCreating(false); };
  const closeEditor = () => { setSelected(null); setCreating(false); };

  const save = async () => {
    if (!selected?.client_name?.trim()) { alert("Client name required"); return; }
    setSaving(true);
    try {
      if (creating) {
        const { data } = await api.post("/quotations", selected);
        setItems(list => [data, ...list]);
        setSelected(data); setCreating(false);
      } else {
        const { data } = await api.put(`/quotations/${selected.id}`, selected);
        setItems(list => list.map(i => i.id === data.id ? data : i));
        setSelected(data);
      }
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quotation?")) return;
    await api.delete(`/quotations/${id}`);
    setItems(list => list.filter(i => i.id !== id));
    if (selected?.id === id) closeEditor();
  };

  const printQuote = () => {
    if (!selected) return;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(renderPrintHTML(selected));
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (_) { /* noop */ } }, 400);
  };

  return (
    <div data-testid="app-quotation" className="h-full w-full flex flex-col bg-slate-50 text-slate-800">
      <Header onNew={startNew} q={q} setQ={setQ} onRefresh={load} loading={loading} />

      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Left list */}
        <div className="w-72 border-r border-slate-200 overflow-y-auto bg-white">
          {filtered.length === 0 && (
            <div className="p-4 text-center text-slate-400 text-xs">No quotations yet. Click &quot;New&quot; to create one.</div>
          )}
          {filtered.map(it => (
            <button key={it.id} onClick={() => openQuote(it)}
              data-testid={`quote-row-${it.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition ${selected?.id === it.id ? "bg-indigo-50 border-l-2 border-l-indigo-500" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-500">{it.quote_number}</span>
                <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded"
                      style={{ background: `${STATUS_COLORS[it.status] || "#94a3b8"}22`, color: STATUS_COLORS[it.status] || "#64748b" }}>
                  {it.status}
                </span>
              </div>
              <div className="text-sm font-semibold text-slate-800 mt-1 truncate">{it.title || it.client_name}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 truncate">{it.client_company || it.client_email || "—"}</div>
              <div className="text-[12px] font-bold text-emerald-600 mt-1">{money(it.currency, it.total)}</div>
            </button>
          ))}
        </div>

        {/* Right: editor / empty state */}
        <div className="flex-1 overflow-y-auto">
          {!selected && (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
              <FileText size={48} className="text-slate-300" />
              <div className="text-sm">Select a quotation on the left, or create a new one.</div>              <button onClick={startNew} className="text-xs font-semibold px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1">
                <Plus size={12} /> New Quotation
              </button>
            </div>
          )}
          {selected && <Editor value={selected} setValue={setSelected} onSave={save} onDelete={() => remove(selected.id)} onPrint={printQuote} saving={saving} isNew={creating} onClose={closeEditor} />}
        </div>
      </div>
    </div>
  );
}

function Header({ onNew, q, setQ, onRefresh, loading }) {
  return (
    <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-md flex items-center justify-center bg-gradient-to-br from-indigo-500 to-cyan-500 text-white shadow-md">
          <FileText size={16} />
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-indigo-600">HEXA · Quotation Maker</div>
          <div className="text-sm font-semibold text-slate-800">Create & manage quotations</div>
        </div>
      </div>
      <div className="flex-1" />
      <div className="flex items-center gap-1 border border-slate-200 rounded px-2 py-1 w-72 bg-white">
        <Search size={12} className="text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by number, client, title…"
               className="flex-1 outline-none text-xs" data-testid="quote-search" />
      </div>
      <button onClick={onRefresh} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Refresh">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
      </button>
      <button onClick={onNew} data-testid="quote-new"
              className="bg-indigo-600 text-white text-xs font-semibold px-3 py-2 rounded flex items-center gap-1 hover:bg-indigo-700">
        <Plus size={12} /> New
      </button>
    </div>
  );
}

function Editor({ value, setValue, onSave, onDelete, onPrint, saving, isNew, onClose }) {
  const setField = (k, v) => setValue(x => ({ ...x, [k]: v }));
  const setItem = (idx, patch) => setValue(x => ({ ...x, items: x.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setValue(x => ({ ...x, items: [...x.items, { description: "", quantity: 1, unit_price: 0, tax_percent: 0 }] }));
  const rmItem = (idx) => setValue(x => ({ ...x, items: x.items.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => computeTotals(value.items || [], value.discount_percent, value.tax_percent), [value]);
  const sym = (CURRENCIES.find(c => c.code === value.currency) || CURRENCIES[0]).symbol;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">{isNew ? "New Quotation" : value.quote_number}</div>
          <h2 className="text-xl font-bold text-slate-800">{isNew ? "Create Quotation" : (value.title || value.client_name || "Quotation")}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} data-testid="quote-print"
                  className="text-xs font-semibold px-3 py-2 rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1 text-slate-700">
            <Printer size={12} /> Print / PDF
          </button>
          {!isNew && (
            <button onClick={onDelete} className="text-xs font-semibold px-3 py-2 rounded border border-red-200 hover:bg-red-50 text-red-600 flex items-center gap-1">
              <Trash2 size={12} /> Delete
            </button>
          )}
          <button onClick={onSave} disabled={saving} data-testid="quote-save"
                  className="text-xs font-semibold px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-1">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-500"><X size={14} /></button>
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-white border border-slate-200 rounded p-4 shadow-sm">
        <F label="Title" cls="md:col-span-2" value={value.title} onChange={(v) => setField("title", v)} />
        <F label="Issue Date" type="date" value={value.issue_date} onChange={(v) => setField("issue_date", v)} />
        <F label="Valid Until" type="date" value={value.valid_until} onChange={(v) => setField("valid_until", v)} />
        <div>
          <L>Status</L>
          <select value={value.status} onChange={(e) => setField("status", e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-2 text-sm">
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Client */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white border border-slate-200 rounded p-4 shadow-sm">
        <F label="Client Name" value={value.client_name} onChange={(v) => setField("client_name", v)} required />
        <F label="Client Company" value={value.client_company} onChange={(v) => setField("client_company", v)} />
        <F label="Client Email" value={value.client_email} onChange={(v) => setField("client_email", v)} />
        <div>
          <L>Currency</L>
          <select value={value.currency} onChange={(e) => setField("currency", e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-2 text-sm">
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
        </div>
        <F label="Client Address" value={value.client_address} onChange={(v) => setField("client_address", v)} cls="md:col-span-4" />
      </div>

      {/* Line items */}
      <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-slate-500 mono">// Line Items</div>
          <button onClick={addItem} data-testid="quote-add-item" className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
            <Plus size={11} /> Add Row
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 mono">
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right w-20">Qty</th>
              <th className="px-3 py-2 text-right w-28">Unit ({sym})</th>
              <th className="px-3 py-2 text-right w-20">Tax %</th>
              <th className="px-3 py-2 text-right w-28">Line Total</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {(value.items || []).map((it, idx) => {
              const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
              return (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <input value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })}
                           placeholder="Item description"
                           data-testid={`quote-item-desc-${idx}`}
                           className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input type="number" value={it.quantity} onChange={(e) => setItem(idx, { quantity: Number(e.target.value) })}
                           className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input type="number" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: Number(e.target.value) })}
                           className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input type="number" value={it.tax_percent} onChange={(e) => setItem(idx, { tax_percent: Number(e.target.value) })}
                           className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right" />
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-slate-800">{money(value.currency, line)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => rmItem(idx)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash2 size={11} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals + Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded p-4 shadow-sm space-y-3">
          <div>
            <L>Notes</L>
            <textarea rows={3} value={value.notes || ""} onChange={(e) => setField("notes", e.target.value)}
                      className="w-full border border-slate-200 rounded p-2 text-sm outline-none focus:border-indigo-400" />
          </div>
          <div>
            <L>Terms & Conditions</L>
            <textarea rows={3} value={value.terms || ""} onChange={(e) => setField("terms", e.target.value)}
                      className="w-full border border-slate-200 rounded p-2 text-sm outline-none focus:border-indigo-400" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded p-4 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            <F label="Discount %" type="number" value={value.discount_percent} onChange={(v) => setField("discount_percent", Number(v))} />
            <F label="Global Tax %" type="number" value={value.tax_percent} onChange={(v) => setField("tax_percent", Number(v))} />
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <Row label="Subtotal" value={money(value.currency, totals.subtotal)} />
            <Row label={`Discount (${value.discount_percent || 0}%)`} value={`- ${money(value.currency, totals.discount)}`} />
            <Row label="Tax" value={money(value.currency, totals.tax)} />
            <div className="pt-2 border-t border-slate-200 flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest text-slate-500 mono">Grand Total</span>
              <span className="text-2xl font-extrabold text-indigo-600" data-testid="quote-grand-total">{money(value.currency, totals.total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}

function L({ children }) { return <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono">{children}</label>; }
function F({ label, value, onChange, type = "text", cls = "", required }) {
  return (
    <div className={cls}>
      <L>{label}{required && <span className="text-red-500"> *</span>}</L>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             className="w-full border border-slate-200 rounded px-3 py-2 text-sm outline-none focus:border-indigo-400" />
    </div>
  );
}

function computeTotals(items, discountPct, globalTax) {
  let subtotal = 0, tax = 0;
  items.forEach(it => {
    const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
    subtotal += line;
    let pct = Number(it.tax_percent) || 0;
    if (!pct) pct = Number(globalTax) || 0;
    tax += line * (pct / 100);
  });
  const disc = subtotal * ((Number(discountPct) || 0) / 100);
  return { subtotal, discount: disc, tax, total: subtotal - disc + tax };
}

function money(ccy, n) {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy || "INR", maximumFractionDigits: 2 }).format(Number(n || 0)); }
  catch { return `$${Number(n || 0).toFixed(2)}`; }
}

function renderPrintHTML(q) {
  const sym = (CURRENCIES.find(c => c.code === q.currency) || CURRENCIES[0]).symbol;
  const rows = (q.items || []).map(it => {
    const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
    return `<tr>
      <td>${escapeHtml(it.description || "")}</td>
      <td style="text-align:right">${it.quantity || 0}</td>
      <td style="text-align:right">${sym}${(Number(it.unit_price) || 0).toFixed(2)}</td>
      <td style="text-align:right">${it.tax_percent || 0}%</td>
      <td style="text-align:right">${sym}${line.toFixed(2)}</td>
    </tr>`;
  }).join("");
  return `<!doctype html><html><head><meta charset="utf-8"/>
    <title>${escapeHtml(q.quote_number || "Quotation")}</title>
    <style>
      body { font-family: 'Inter', system-ui, sans-serif; color: #1e293b; margin: 40px; }
      h1 { color: #6366f1; margin: 0 0 4px; font-size: 32px; }
      .muted { color: #64748b; font-size: 12px; text-transform: uppercase; letter-spacing: 2px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin: 24px 0; }
      table { width: 100%; border-collapse: collapse; margin: 24px 0; }
      th, td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
      th { text-align: left; background: #f1f5f9; text-transform: uppercase; letter-spacing: 1px; font-size: 10px; color: #64748b; }
      .totals { margin-left: auto; width: 320px; }
      .total-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; }
      .grand { font-size: 22px; font-weight: 800; color: #6366f1; border-top: 2px solid #6366f1; padding-top: 8px; margin-top: 8px; }
      .status { display: inline-block; padding: 2px 10px; background: ${STATUS_COLORS[q.status] || "#94a3b8"}22; color: ${STATUS_COLORS[q.status] || "#64748b"}; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; font-weight: 800; border-radius: 3px; }
      .notes { font-size: 12px; color: #475569; margin-top: 24px; padding: 12px; background: #f8fafc; border-left: 3px solid #6366f1; }
      @media print { body { margin: 20px; } }
    </style></head>
    <body>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;">
        <div>
          <div class="muted">Quotation</div>
          <h1>${escapeHtml(q.quote_number || "")}</h1>
          <div class="status">${escapeHtml(q.status || "draft")}</div>
        </div>
        <div style="text-align:right">
          <div class="muted">Issued</div>
          <div style="font-weight:600">${escapeHtml(q.issue_date || "—")}</div>
          <div class="muted" style="margin-top:8px">Valid Until</div>
          <div style="font-weight:600">${escapeHtml(q.valid_until || "—")}</div>
        </div>
      </div>
      <div class="grid">
        <div>
          <div class="muted">Bill To</div>
          <div style="font-weight:700;font-size:16px;margin-top:4px">${escapeHtml(q.client_name || "")}</div>
          <div>${escapeHtml(q.client_company || "")}</div>
          <div>${escapeHtml(q.client_email || "")}</div>
          <div style="white-space:pre-line;font-size:12px;color:#64748b;margin-top:4px">${escapeHtml(q.client_address || "")}</div>
        </div>
        <div>
          <div class="muted">Title</div>
          <div style="font-weight:700;font-size:16px;margin-top:4px">${escapeHtml(q.title || "")}</div>
        </div>
      </div>
      <table>
        <thead><tr><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Tax</th><th style="text-align:right">Line Total</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="5" style="text-align:center;color:#94a3b8">No line items.</td></tr>`}</tbody>
      </table>
      <div class="totals">
        <div class="total-row"><span>Subtotal</span><span>${sym}${(Number(q.subtotal) || 0).toFixed(2)}</span></div>
        <div class="total-row"><span>Discount (${q.discount_percent || 0}%)</span><span>- ${sym}${(Number(q.discount) || 0).toFixed(2)}</span></div>
        <div class="total-row"><span>Tax</span><span>${sym}${(Number(q.tax) || 0).toFixed(2)}</span></div>
        <div class="total-row grand"><span>Grand Total</span><span>${sym}${(Number(q.total) || 0).toFixed(2)}</span></div>
      </div>
      ${q.notes ? `<div class="notes"><strong>Notes:</strong> ${escapeHtml(q.notes)}</div>` : ""}
      ${q.terms ? `<div class="notes"><strong>Terms:</strong> ${escapeHtml(q.terms)}</div>` : ""}
    </body></html>`;
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c] || c));
}
